import {Chip} from "@heroui/react";
import {Spinner} from "@heroui/spinner";
import {useQuery} from "@tanstack/react-query";
import {createFileRoute, Link} from '@tanstack/react-router'
import React, {lazy, Suspense, useState} from "react";
import {apiRetrieveImproved} from "../../../api";
import Header from "../../../Header.tsx";
import {deviceLabel} from "../../../utilities/deviceLabel.ts";
import {barLabel, pctLabel, Split, splitPercents} from "../../../master/quizSplitRules.ts";

const Chart = lazy(() => import("react-apexcharts"));

export const Route = createFileRoute('/_app/master/quiz-responses')({
    component: RouteComponent,
})

type QuizResponseRow = {
    uid: string;
    /** Which quiz the response came from: 'main' or 'community'. */
    variant?: 'main' | 'community' | string;
    answers: Record<string, string> | null;
    completed: boolean;
    plan?: 'free' | 'event_pass' | null;
    mode?: 'basic' | 'advanced' | null;
    pass_paid?: boolean;
    entrants?: number | null;
    has_scores?: boolean;
    /** Draw generated (tournaments) / schedule of games on the board (leagues). Null = no event. */
    draw_created?: boolean | null;
    source_url?: string;
    /** Ad campaign / traffic source, derived server-side from source_url (works for historic rows). */
    campaign?: string | null;
    /** ISO country from the Cloudflare edge at submit time — null on rows predating capture. */
    country_code?: string | null;
    /** Raw User-Agent at submit time — null on rows predating capture; summarized via deviceLabel. */
    user_agent?: string | null;
    converted_at?: string;
    /** Their answer to the signup-step exit question — null when never asked or not answered. */
    exit_reason?: ExitReason | null;
    /** Free text, kept for 'other' only. */
    exit_note?: string | null;
    tournament_uid?: string;
    created_at?: string;
    user?: {uid: string; name?: string; lastname?: string; email?: string} | null;
    /** Stored attribution via the users_analytics.quiz_response_uid join — null when unlinked. */
    attribution?: {
        utm_source?: string | null;
        utm_campaign?: string | null;
        click_id_type?: 'gclid' | 'gbraid' | 'wbraid' | 'click_id' | null;
        confidence?: 'recorded' | 'inferred' | 'none' | null;
    } | null;
};

type ExitReason = 'no_info' | 'unclear_value' | 'other' | 'continue';

/** The signup exit question's options, in the order the popup shows them. */
const EXIT_REASONS: {key: ExitReason; label: string}[] = [
    {key: 'no_info', label: "Don't want to provide information"},
    {key: 'unclear_value', label: "Don't know what I'm getting"},
    {key: 'other', label: 'Other'},
    {key: 'continue', label: 'Continued to signup'},
];
const EXIT_LABEL = Object.fromEntries(EXIT_REASONS.map((r) => [r.key, r.label])) as Record<ExitReason, string>;

/** Same numbers as `artisan attribution:check` — served by the same backend service. */
type AttributionSummary = {
    window_days: number;
    confidence: {recorded: number; inferred: number; none: number; null: number};
    id_types: Record<string, number>;
    quiz: {total: number; with_click_id: number};
    orphans: {total: number; no_analytics_row: number; no_click_id: number; converted: number};
    campaigns: Record<string, number>;
};

const CONFIDENCE_CHIP: Record<string, {color: 'success' | 'warning' | 'default'; label: string}> = {
    recorded: {color: 'success', label: 'Recorded'},
    inferred: {color: 'warning', label: 'Inferred'},
    none:     {color: 'default', label: 'None'},
};

// Question order and readable answer labels — mirrors the /quiz STEPS (keys '1'..'5').
const QUESTION_TITLES: Record<string, string> = {
    audience: 'Audience', 1: 'Organizing', 2: 'Sport', 3: 'Headache', 4: 'Fees today', 5: 'Size',
};

// The community funnel (/quiz-community) answers named keys, not '1'..'5', so it gets its own
// columns — shown when that funnel is selected. 'features' is a multi-select, 'community' free text.
const COMMUNITY_TITLES: Record<string, string> = {
    size: 'Size', features: 'Wants', sport: 'Sport', community: 'Community name', next_step: 'Way in',
};
// The pricing funnel (/scorepal-quiz/): how they collect money and would pay. 'time_savers' is a multi-select.
const PRICING_TITLES: Record<string, string> = {
    sport: 'Sport', collect: 'Collects money', time_savers: 'Saves most time', pay_model: 'Would pay',
    pass_16: '$19.99 for 16 feels', blocker: 'Would stop them', next_step: 'Next',
};
const ANSWER_LABELS: Record<string, Record<string, string>> = {
    1: {tournament: 'Tournament', league: 'League', ladder: 'Ladder', social: 'Americano'},
    2: {
        padel: 'Padel', pickleball: 'Pickleball', tennis: 'Tennis', badminton: 'Badminton', squash: 'Squash',
        football: 'Football', basketball: 'Basketball', volleyball: 'Volleyball', cricket: 'Cricket', esports: 'Esports',
        roundnet: 'Roundnet', other: 'Another sport',
    },
    3: {payments: 'Chasing payments', signups: 'Collecting signups', scores: 'Score keeping', schedule: 'Sharing schedules'},
    4: {cash: 'Cash', transfer: 'Bank transfers', mixed: 'Mixed/messy', free: 'Free events'},
    5: {small: 'Up to 8', medium: '9–16', large: '17–32', xl: '32+'},
    // The audience split that opens /quiz, and the community funnel's own questions.
    audience: {community: 'Club / community', one_off: 'One-off event'},
    size: {'4_10': '4–10', '11_30': '11–30', '31_75': '31–75', '75_plus': '75+'},
    sport: {
        padel: 'Padel', pickleball: 'Pickleball', table_tennis: 'Table tennis', tennis: 'Tennis',
        badminton: 'Badminton', squash: 'Squash', football: 'Football / Soccer', basketball: 'Basketball',
        volleyball: 'Volleyball', cricket: 'Cricket', esports: 'Esports', roundnet: 'Roundnet', other: 'Another sport',
    },
    features: {
        registration: 'Registration & payments', fixtures: 'Fixtures & scheduling', live_scores: 'Live scores & standings',
        live_updates: 'Live event updates', players: 'Players & teams', crm: 'Player / team CRM',
        match_day: 'Match-day check-in', engagement: 'Keeping players coming back', comms: 'Messaging players',
        club_page: 'A public club page', streaming: 'Streaming', notifications: 'Player notifications',
        player_app: 'Player app', events: 'Events',
    },
    // The two buttons on the community quiz's last screen, worded as the visitor saw them.
    next_step: {explore: 'Explore features with a free account', setup_event: 'Set up a new event now', create: 'Create my event now'},
    // The pricing quiz's answers, worded as the visitor saw them.
    collect: {cash_transfer: 'Cash or bank transfer on the day', payment_links: 'Payment links (PayPal, Stripe…)', booking_app: 'Booking app / venue system', free: 'Free to play'},
    time_savers: {
        self_registration: 'Players register and pay themselves', whatsapp: 'Sign-ups through WhatsApp', automation: 'Automatic draws, fixtures, standings',
        live_scores: 'Live scores players can follow', tracking: 'Tracking players and who owes',
    },
    pay_model: {per_event: 'Per event', yearly: 'One yearly payment', monthly: 'Monthly subscription', registration_fee: 'Small fee from registrations', free_only: 'Free tools only'},
    pass_16: {cheap: 'Cheap, pay without thinking', fair: 'Fair, would pay', expensive_but_worth: 'Expensive, but would pay', too_expensive: 'Too expensive'},
    blocker: {infrequent: 'Don’t run events often enough', players_wont_switch: 'Players won’t use a new app', free_tools: 'Free tools / WhatsApp are enough', see_it_first: 'Need to see it work first', price: 'The price'},
};

// The question as the visitor saw it — the chart titles should read the same way.
const QUESTION_TEXT: Record<string, string> = {
    1: 'What are you organizing next?',
    2: 'Which sport?',
    3: "What's the biggest headache when you run events?",
    4: 'How do you handle event fees today?',
    5: 'How many teams in your next event?',
    audience: 'Which one sounds like you?',
    size: 'How big is your community or club?',
    features: 'What would help you most?',
    sport: 'What sport do you manage?',
    // Not a question but a screen with two buttons; the title is its heading.
    next_step: "Here's what you'll be running",
    collect: 'How do you collect money from players today?',
    time_savers: 'Which of these would save you the most time? (up to 2)',
    pay_model: 'How would you prefer to pay for a tool like ScorePal?',
    pass_16: 'For an event of 16 players, a $19.99 Event Pass feels:',
    blocker: 'What’s the main thing that would stop you paying?',
};


/** Answers by step, over every response (the table is capped at 500 rows; this is not). */
/** One country's share of an answer (or of everyone); '??' is pre-capture, '++' the folded tail. */
type CountrySplit = {code: string} & Split;
type AnswerBreakdown = {
    total: number;
    /** Each option carries its own countries, biggest first — opened by clicking the answer. */
    steps: {step: string; answered: number; options: ({val: string; countries?: CountrySplit[]} & Split)[]}[];
    /** Typed "Another sport" entries, lower-cased and tallied. */
    other_sports: {val: string; count: number}[];
    /** Cloudflare edge country at submit time; '??' is the pre-capture bucket. */
    countries: ({code: string} & Split)[];
};

const pctOf = (n: number, of: number) => of > 0 ? Math.round((n / of) * 100) + '%' : '—';

const countryName = (code: string): string => {
    if (code === '??') return 'Unknown';
    if (code === '++') return 'Other countries'; // everything past the cap, folded so the rows add up
    try {
        return new Intl.DisplayNames(['en'], {type: 'region'}).of(code) || code;
    } catch {
        return code; // an edge code that is not an ISO region (e.g. Tor's "T1")
    }
};

/**
 * Three horizontal bars per row — clicked/started, signed up, and entered scores — so a card reads
 * as a drop-off point and a conversion split at the same time.
 */
function SplitBars({title, subtitle, categories, rows, emptyText, answered, shareLabel, children}: {
    title: React.ReactNode; subtitle?: React.ReactNode; categories: string[];
    /** A row that carries `countries` can be clicked — bar or table row — to open them underneath. */
    rows: (Split & {countries?: CountrySplit[]})[]; emptyText: string;
    /** What "Clicked" is a share of: the people who answered this question (or everyone, for countries). */
    answered: number; shareLabel: string; children?: React.ReactNode;
}) {
    // Which answer's countries are open. One at a time: the point is to compare countries within
    // an answer, and two open tables of near-identical rows are harder to read than one.
    const [open, setOpen] = useState<number | null>(null);
    const canOpen = (i: number) => (rows[i]?.countries?.length ?? 0) > 0;
    const toggle = (i: number) => { if (canOpen(i)) setOpen((cur) => (cur === i ? null : i)); };
    const drillable = rows.some((_, i) => canOpen(i));

    const series = [
        {name: 'Clicked', data: rows.map((r) => r.count)},
        {name: 'Signed up', data: rows.map((r) => r.converted)},
        {name: 'Entered scores', data: rows.map((r) => r.scored)},
    ];
    const height = Math.max(190, 56 * rows.length + 70);
    const chartOptions = {
        chart:       {
            toolbar: {show: false}, background: 'transparent', fontFamily: 'inherit', animations: {enabled: false},
            // Clicking any of an answer's three bars opens that answer's countries, same as its row.
            events:  {dataPointSelection: (_e: unknown, _ctx: unknown, cfg: {dataPointIndex?: number}) => {
                if (typeof cfg?.dataPointIndex === 'number') toggle(cfg.dataPointIndex);
            }},
        },
        plotOptions: {bar: {horizontal: true, borderRadius: 3, barHeight: '75%'}},
        colors:      ['#193088', '#17c964', '#f5a524'],
        // Count, then the percentage THAT bar stands for: clicked = share of answers, signed up =
        // of those who clicked, entered scores = of those who signed up.
        dataLabels:  {
            enabled:   true,
            formatter: (v: number, o: any) => (rows[o?.dataPointIndex] ? barLabel(o.seriesIndex, rows[o.dataPointIndex], answered) : (v > 0 ? String(v) : '')),
            style:     {fontSize: '11px'},
        },
        xaxis:       {categories, labels: {style: {colors: '#71717a', fontSize: '11px'}}, axisBorder: {show: false}, decimalsInFloat: 0},
        yaxis:       {labels: {style: {colors: '#3f3f46', fontSize: '12px'}, maxWidth: 220}},
        grid:        {borderColor: '#e4e4e7', strokeDashArray: 3, xaxis: {lines: {show: true}}, yaxis: {lines: {show: false}}},
        legend:      {show: true, position: 'top' as const, horizontalAlign: 'left' as const, fontSize: '11px', labels: {colors: '#71717a'}},
        tooltip:     {y: {formatter: (v: number, o: any) => (rows[o?.dataPointIndex] ? barLabel(o.seriesIndex, rows[o.dataPointIndex], answered) || String(v) : String(v))}},
        // No "selected" tint either: the chart would keep a bar darkened after its countries were
        // closed again. The highlighted table row underneath is what says which answer is open.
        states:      {hover: {filter: {type: 'none'}}, active: {filter: {type: 'none'}}},
    };

    return (
        <div className="rounded-xl border border-default-200 bg-content1 p-4">
            <div className="mb-1 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <h3 className="text-sm font-semibold">{title}</h3>
                {subtitle && <span className="text-xs text-default-400">{subtitle}</span>}
            </div>
            {rows.length === 0 ? (
                <div className="py-8 text-center text-sm text-default-400">{emptyText}</div>
            ) : (
                <Suspense fallback={<div className="flex items-center justify-center" style={{height}}><Spinner size="sm" /></div>}>
                    <Chart options={chartOptions as any} series={series} type="bar" height={height} />
                </Suspense>
            )}
            {/* The same numbers as a table: a bar of 7 next to a bar of 1,085 has no room for its
                label, and the step-to-step conversion is the thing being read. */}
            {rows.length > 0 && (
                <div className="mt-1 overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="border-b border-default-200 text-left text-[10px] uppercase tracking-wider text-default-400">
                                <th className="py-1 pr-2 font-medium"></th>
                                <th className="px-2 py-1 text-right font-medium">Clicked</th>
                                <th className="px-2 py-1 text-right font-medium">{shareLabel}</th>
                                <th className="px-2 py-1 text-right font-medium">Signed up</th>
                                <th className="px-2 py-1 text-right font-medium">of clicked</th>
                                <th className="px-2 py-1 text-right font-medium">Entered scores</th>
                                <th className="px-2 py-1 text-right font-medium">of signed up</th>
                                <th className="py-1 pl-2 text-right font-medium">of clicked</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => {
                                const p = splitPercents(r, answered);
                                const isOpen = open === i;
                                return (
                                    <React.Fragment key={categories[i] ?? i}>
                                        <tr className={'border-b border-default-100 last:border-0 ' + (canOpen(i) ? 'cursor-pointer hover:bg-default-50 ' : '') + (isOpen ? 'bg-primary/5' : '')}
                                            onClick={() => toggle(i)}
                                            title={canOpen(i) ? (isOpen ? 'Hide the countries' : 'Show this answer by country') : undefined}>
                                            <td className="max-w-[160px] truncate py-1 pr-2 text-default-700" title={categories[i]}>
                                                {canOpen(i) && <span className="mr-1 inline-block w-2 text-default-400" aria-hidden="true">{isOpen ? '▾' : '▸'}</span>}
                                                {categories[i]}
                                            </td>
                                            <td className="px-2 py-1 text-right tabular-nums">{r.count}</td>
                                            <td className="px-2 py-1 text-right tabular-nums text-default-400">{pctLabel(p.clickedOfAnswered)}</td>
                                            <td className="px-2 py-1 text-right tabular-nums">{r.converted}</td>
                                            <td className="px-2 py-1 text-right tabular-nums font-semibold text-success-600">{pctLabel(p.signedUpOfClicked)}</td>
                                            <td className="px-2 py-1 text-right tabular-nums">{r.scored}</td>
                                            <td className="px-2 py-1 text-right tabular-nums font-semibold text-warning-600">{pctLabel(p.scoredOfSignedUp)}</td>
                                            <td className="py-1 pl-2 text-right tabular-nums text-default-400">{pctLabel(p.scoredOfClicked)}</td>
                                        </tr>
                                        {/* The answer's countries, in the same columns. Their "share" is of THIS
                                            answer, not of the question — the question here is "who picked it?". */}
                                        {isOpen && r.countries!.map((c) => {
                                            const cp = splitPercents(c, r.count);
                                            return (
                                                <tr key={`${categories[i]}-${c.code}`} className="border-b border-default-100 bg-default-50/60 text-default-600 last:border-0" data-answer-country={c.code}>
                                                    <td className="max-w-[160px] truncate py-1 pl-5 pr-2" title={countryName(c.code)}>{countryName(c.code)}</td>
                                                    <td className="px-2 py-1 text-right tabular-nums">{c.count}</td>
                                                    <td className="px-2 py-1 text-right tabular-nums text-default-400" title="Share of this answer">{pctLabel(cp.clickedOfAnswered)}</td>
                                                    <td className="px-2 py-1 text-right tabular-nums">{c.converted}</td>
                                                    <td className="px-2 py-1 text-right tabular-nums font-semibold text-success-600">{pctLabel(cp.signedUpOfClicked)}</td>
                                                    <td className="px-2 py-1 text-right tabular-nums">{c.scored}</td>
                                                    <td className="px-2 py-1 text-right tabular-nums font-semibold text-warning-600">{pctLabel(cp.scoredOfSignedUp)}</td>
                                                    <td className="py-1 pl-2 text-right tabular-nums text-default-400">{pctLabel(cp.scoredOfClicked)}</td>
                                                </tr>
                                            );
                                        })}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                    {drillable && <div className="mt-1 text-[10px] text-default-400">Click an answer, or its bar, to see it by country.</div>}
                </div>
            )}
            {children}
        </div>
    );
}

/**
 * The whole journey in four stages — entered the quiz, dropped out, signed up, entered scores —
 * each with its count, its share of everyone who entered, and (from the third stage on) its share
 * of the stage before. The bar under each stage is its share of entrants, so the narrowing reads
 * at a glance.
 */
function Funnel({entered, completed, converted, scored}: {entered: number; completed: number; converted: number; scored: number}) {
    const dropped = entered - converted;
    const midQuiz = entered - completed;
    const atSignup = completed - converted;
    const stages: {label: string; value: number; share: number; sub: React.ReactNode; color: string}[] = [
        {label: 'Entered the quiz', value: entered, share: 1, sub: 'answered at least one question', color: 'bg-primary'},
        {
            label: 'Dropped out', value: dropped, share: entered > 0 ? dropped / entered : 0,
            sub: <>{pctOf(dropped, entered)} of entered · {midQuiz} mid-quiz · {atSignup} at signup</>, color: 'bg-danger',
        },
        {label: 'Signed up', value: converted, share: entered > 0 ? converted / entered : 0, sub: <>{pctOf(converted, entered)} of entered</>, color: 'bg-success'},
        {
            label: 'Entered scores', value: scored, share: entered > 0 ? scored / entered : 0,
            sub: <>{pctOf(scored, converted)} of signups · {pctOf(scored, entered)} of entered</>, color: 'bg-warning',
        },
    ];

    return (
        <div className="mb-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
            {stages.map((s, i) => (
                <div key={s.label} className="relative rounded-xl border border-default-200 bg-content1 p-4">
                    {i > 0 && <span className="absolute -left-3 top-1/2 hidden -translate-y-1/2 text-default-300 lg:block">›</span>}
                    <div className="text-2xl font-bold tabular-nums">{s.value}</div>
                    <div className="text-sm text-default-500">{s.label}</div>
                    <div className="text-xs text-default-400">{s.sub}</div>
                    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-default-100">
                        <div className={`h-full rounded-full ${s.color}`} style={{width: `${Math.round(s.share * 100)}%`}} />
                    </div>
                </div>
            ))}
        </div>
    );
}

/**
 * One answer cell. Free-text follow-ups are shown in place of the option that triggered them, and
 * the community funnel's multi-select becomes one chip per pick rather than a comma-joined blob.
 */
function answerCell(r: QuizResponseRow, k: string): React.ReactNode {
    const raw = r.answers?.[k];
    const dash = <span className="text-default-300">—</span>;

    // "Another sport" carries the typed sport in answers['2_other'] — show that.
    if (k === '2' && raw === 'other' && r.answers?.['2_other']) return r.answers['2_other'];

    if (k === 'features') {
        const picks = (raw || '').split(',').map((f) => f.trim()).filter(Boolean);
        const other = r.answers?.features_other;
        if (!picks.length && !other) return dash;
        return (
            <div className="flex max-w-[24rem] flex-wrap gap-1">
                {picks.map((f) => <Chip key={f} size="sm" variant="flat">{ANSWER_LABELS.features?.[f] || f}</Chip>)}
                {other && <Chip size="sm" color="warning" variant="flat">{other}</Chip>}
            </div>
        );
    }

    if (k === 'time_savers') {
        const picks = (raw || '').split(',').map((f) => f.trim()).filter(Boolean);
        if (!picks.length) return dash;
        return <div className="flex max-w-[24rem] flex-wrap gap-1">{picks.map((f) => <Chip key={f} size="sm" variant="flat">{ANSWER_LABELS.time_savers?.[f] || f}</Chip>)}</div>;
    }

    if (!raw) return dash;
    return <span className="whitespace-nowrap">{ANSWER_LABELS[k]?.[raw] || raw}</span>;
}

/** One question, options in the order the visitor saw them; anything unexpected (retired values) goes last. */
function StepChart({step, position, answered, dropped, options, total, children}: {
    step: string; position: number; answered: number; dropped: number | null; options: AnswerBreakdown['steps'][number]['options']; total: number; children?: React.ReactNode;
}) {
    const known = Object.keys(ANSWER_LABELS[step] || {});
    const byVal = new Map(options.map((o) => [o.val, o]));
    // Every option the visitor was offered, in the order they saw it — including the ones nobody
    // picked. Dropping the empty bars hid half the question: "1 chose this" reads very differently
    // when you can see the other choice was on screen and got nothing.
    const ordered = [
        ...known.map((val) => byVal.get(val) ?? {val, count: 0, converted: 0, scored: 0}),
        // Retired or unrecognised values still have real clicks behind them: last, not gone.
        ...options.filter((o) => !known.includes(o.val)),
    ];

    return (
        <SplitBars
            // Numbered by position, not by key: the community funnel's questions are named, and
            // "audience. Which one sounds like you?" reads like a typo.
            title={<>{position}. {QUESTION_TEXT[step] || QUESTION_TITLES[step]}</>}
            subtitle={<>
                {answered} answered · {pctOf(answered, total)} of entered
                {dropped != null && dropped > 0 && <> · <span className="text-danger">{dropped} dropped here</span></>}
            </>}
            categories={ordered.map((o) => ANSWER_LABELS[step]?.[o.val] || o.val)}
            rows={ordered}
            answered={answered}
            shareLabel="of answers"
            emptyText="No answers yet."
        >
            {children}
        </SplitBars>
    );
}

function StatCard({label, value, sub}: {label: string; value: React.ReactNode; sub?: string}) {
    return (
        <div className="rounded-xl border border-default-200 bg-content1 p-4">
            <div className="text-2xl font-bold">{value}</div>
            <div className="text-sm text-default-500">{label}</div>
            {sub && <div className="text-xs text-default-400">{sub}</div>}
        </div>
    );
}

function RouteComponent() {
    return <QuizResponsesPage />;
}

/** Fixed to one funnel (`locked`), the funnel tabs are hidden and the page carries that funnel's own title. */
export function QuizResponsesPage({locked, title = 'Quiz Responses'}: {locked?: 'main' | 'community' | 'pricing'; title?: string} = {}) {
    // Which funnel: the main quiz, the community quiz (tagged variant=community), or both. Every
    // number on the page — funnel, answers by step, rows — follows the choice; attribution is
    // whole-site and stays as it is.
    const [funnel, setFunnel] = useState<'all' | 'main' | 'community' | 'pricing'>(locked ?? 'all');
    // The time window, in days; 0 is all time. Every number on the page follows it too.
    const [days, setDays] = useState(0);
    const query = useQuery<{stats: {window_days?: number; total: number; completed: number; converted: number; left_at_signup?: number; exit_asked?: number; exit_reasons?: Partial<Record<ExitReason, number>> | []; with_tournament: number; pass_clicks: number; scored?: number}; attribution?: AttributionSummary; answers?: AnswerBreakdown; rows: QuizResponseRow[]}>({
        queryKey: ['master-quiz-responses', funnel, days],
        queryFn:  () => apiRetrieveImproved('master/quiz-responses' as any, {variant: funnel, days}),
    });

    const stats = query.data?.stats;
    const attr = query.data?.attribution;
    const answers = query.data?.answers;
    // Tolerate a response from before a key existed (the API deploys ahead of the frontend).
    // The community funnel answers named keys; the main quiz answers '1'..'5'. Show the columns
    // belonging to whichever funnel is selected rather than a row of dashes for the other one.
    const columnTitles = funnel === 'community' ? COMMUNITY_TITLES : funnel === 'pricing' ? PRICING_TITLES : QUESTION_TITLES;
    const countries = answers?.countries || [];
    const otherSports = answers?.other_sports || [];
    const rows = query.data?.rows || [];
    const pct = (n: number, of: number) => of > 0 ? Math.round((n / of) * 100) + '%' : '—';
    // An empty breakdown arrives as [] (PHP), so read it through a lookup that tolerates both.
    const exitCount = (k: ExitReason) => Number((stats?.exit_reasons as Record<string, number> | undefined)?.[k] || 0);
    const exitAsked = stats?.exit_asked || 0;
    const exitUnanswered = Math.max(0, exitAsked - EXIT_REASONS.reduce((n, r) => n + exitCount(r.key), 0));

    return (
        <>
            <Header />
            <div className="mx-auto w-11/12 max-w-6xl py-4">
                <h1 className="mb-1 text-2xl font-bold">{title}</h1>
                {!locked && <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="quiz-funnel-filter">
                    <span className="text-xs font-semibold uppercase tracking-wider text-default-500">Funnel</span>
                    {([['all', 'All'], ['main', 'Main quiz'], ['community', 'Community quiz'], ['pricing', 'Pricing quiz']] as const).map(([key, label]) => (
                        <button key={key} type="button" onClick={() => setFunnel(key)}
                                className={`rounded-full px-3 py-1 text-xs font-semibold transition ${funnel === key ? 'bg-primary text-white' : 'bg-default-100 text-default-600 hover:bg-default-200'}`}>
                            {label}
                        </button>
                    ))}
                </div>}
                <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="quiz-window-filter">
                    <span className="text-xs font-semibold uppercase tracking-wider text-default-500">Period</span>
                    {([[0, 'All time'], [7, '7d'], [14, '14d'], [30, '30d'], [60, '60d'], [90, '90d'], [180, '180d'], [365, '365d']] as const).map(([d, label]) => (
                        <button key={d} type="button" onClick={() => setDays(d)}
                                className={`rounded-full px-3 py-1 text-xs font-semibold transition ${days === d ? 'bg-primary text-white' : 'bg-default-100 text-default-600 hover:bg-default-200'}`}>
                            {label}
                        </button>
                    ))}
                </div>
                <p className="mb-4 text-sm text-default-500">
                    Every /quiz visitor who answered at least one question — including drop-offs{days > 0 ? `, in the last ${days} days` : ''}. Rows update as visitors progress.
                </p>

                {query.isLoading ? (
                    <div className="flex justify-center py-12"><Spinner size="lg" /></div>
                ) : (
                    <>
                        {stats && (
                            <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
                                <StatCard label="Quizzes started" value={stats.total} />
                                <StatCard label="Completed all 5" value={stats.completed} sub={pct(stats.completed, stats.total) + ' of started'} />
                                {stats.left_at_signup != null && (
                                    <StatCard label="Left at signup" value={stats.left_at_signup} sub={pct(stats.left_at_signup, stats.completed) + ' of completed'} />
                                )}
                                <StatCard label="Signed up" value={stats.converted} sub={pct(stats.converted, stats.total) + ' of started'} />
                                <StatCard label="Tournaments created" value={stats.with_tournament} sub={pct(stats.with_tournament, stats.converted) + ' of signups'} />
                                {stats.scored != null && (
                                    <StatCard label="Entered scores" value={stats.scored} sub={pct(stats.scored, stats.converted) + ' of signups · as on Activation'} />
                                )}
                                <StatCard label="Clicked Event Pass" value={stats.pass_clicks} sub={pct(stats.pass_clicks, stats.converted) + ' of signups'} />
                            </div>
                        )}

                        {/* The signup exit question: of those shown it, what they said. */}
                        {stats?.exit_asked != null && (
                            <div className="mb-6">
                                <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                                    <h2 className="text-sm font-semibold">Why they left at signup</h2>
                                    <span className="text-xs text-default-400">
                                        asked {exitAsked} {exitAsked === 1 ? 'visitor' : 'visitors'} as they went to leave the signup step
                                    </span>
                                </div>
                                {exitAsked === 0 ? (
                                    <p className="rounded-xl border border-default-200 bg-content1 p-4 text-sm text-default-500">
                                        No one has been asked yet.
                                    </p>
                                ) : (
                                    <div className="space-y-2 rounded-xl border border-default-200 bg-content1 p-4">
                                        {[...EXIT_REASONS.map((r) => ({label: r.label, n: exitCount(r.key)})), {label: 'Closed without answering', n: exitUnanswered}].map((r) => (
                                            <div key={r.label} className="grid grid-cols-[minmax(0,14rem)_1fr_auto] items-center gap-3 text-sm">
                                                <span className="truncate">{r.label}</span>
                                                <div className="h-2 overflow-hidden rounded-full bg-default-100">
                                                    <div className="h-full rounded-full bg-primary" style={{width: pct(r.n, exitAsked) === '—' ? 0 : pct(r.n, exitAsked)}} />
                                                </div>
                                                <span className="whitespace-nowrap text-default-500">{r.n} · {pct(r.n, exitAsked)}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Which option got the clicks at each question — every response, not just the rows below. */}
                        {answers && answers.total > 0 && (
                            <div className="mb-6">
                                <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                                    <h2 className="text-sm font-semibold">Answers by step</h2>
                                    <span className="text-xs text-default-400">
                                        all {answers.total} responses — clicks per option, how many of them signed up, and how many went on to enter scores
                                    </span>
                                </div>
                                {stats && (
                                    <Funnel entered={answers.total} completed={stats.completed} converted={stats.converted} scored={stats.scored ?? 0} />
                                )}
                                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                                    {answers.steps.map((s, i) => (
                                        <StepChart
                                            key={s.step} step={s.step} position={i + 1} answered={s.answered} options={s.options} total={answers.total}
                                            // Dropped here = answered this question but not the next one; the last question's
                                            // leavers are the "at signup" drop in the funnel above.
                                            dropped={answers.steps[i + 1] ? s.answered - answers.steps[i + 1].answered : null}>
                                            {s.step === '2' && otherSports.length > 0 && (
                                                <div className="mt-2 flex flex-wrap items-center gap-1">
                                                    <span className="mr-1 text-[10px] uppercase tracking-wider text-default-400">Typed for "Another sport":</span>
                                                    {otherSports.map((o) => (
                                                        <Chip key={o.val} size="sm" variant="flat">{o.val} · {o.count}</Chip>
                                                    ))}
                                                </div>
                                            )}
                                        </StepChart>
                                    ))}
                                    {/* Where they came from — the edge country at submit time, same three-way split. */}
                                    <SplitBars
                                        title="Where they came from"
                                        subtitle={<>{countries.filter((c) => c.code !== '??').length} countries</>}
                                        categories={countries.map((c) => countryName(c.code))}
                                        rows={countries}
                                        answered={answers.total}
                                        shareLabel="of entered"
                                        emptyText="No location recorded yet."
                                    />
                                </div>
                            </div>
                        )}

                        {/* Same numbers as `artisan attribution:check` (same backend service). */}
                        {attr && (
                            <div className="mb-6 rounded-xl border border-default-200 bg-content1 p-4">
                                <div className="mb-3 flex items-baseline justify-between">
                                    <h2 className="text-sm font-semibold">Attribution — last {attr.window_days} days</h2>
                                    <span className="text-xs text-default-400">matches artisan attribution:check</span>
                                </div>
                                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                                    <StatCard label="Recorded" value={attr.confidence.recorded} sub="click id captured" />
                                    <StatCard label="Inferred" value={attr.confidence.inferred} sub="UTMs only" />
                                    <StatCard label="None" value={attr.confidence.none} sub="direct" />
                                    <StatCard label="Pre-measure" value={attr.confidence.null} sub="predates capture — not 'none'" />
                                    <StatCard label="Quiz w/ click id" value={attr.quiz.with_click_id} sub={`of ${attr.quiz.total} quizzes`} />
                                    <StatCard
                                        label="Broken chain"
                                        value={<span className={attr.orphans.total > 0 ? 'text-danger' : undefined}>{attr.orphans.total}</span>}
                                        sub={`${attr.orphans.no_analytics_row} no analytics row · ${attr.orphans.no_click_id} no click id · of ${attr.orphans.converted} conversions`}
                                    />
                                </div>
                                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-default-500">
                                    {Object.entries(attr.id_types).map(([k, v]) => (
                                        <span key={k}>{k}: <span className="font-medium text-default-700">{v}</span></span>
                                    ))}
                                </div>
                                {Object.keys(attr.campaigns).length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-default-500">
                                        <span className="uppercase tracking-wider text-default-400">Campaigns:</span>
                                        {Object.entries(attr.campaigns).map(([name, c]) => (
                                            <span key={name}>{name}: <span className="font-medium text-default-700">{c}</span></span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {rows.length === 0 ? (
                            <div className="py-12 text-center text-default-500">No quiz responses yet.</div>
                        ) : (
                            <div className="overflow-x-auto rounded-xl border border-default-200">
                                <table className="w-full min-w-[1250px] text-sm">
                                    <thead>
                                        <tr className="border-b border-default-200 bg-default-50 text-left text-xs uppercase tracking-wider text-default-500">
                                            <th className="px-3 py-2">Date</th>
                                            <th className="px-3 py-2">Quiz</th>
                                            {Object.keys(columnTitles).map((k) => (
                                                <th key={k} className="px-3 py-2">{columnTitles[k]}</th>
                                            ))}
                                            <th className="px-3 py-2">Setup</th>
                                            <th className="px-3 py-2">Plan</th>
                                            <th className="px-3 py-2">Draw</th>
                                            <th className="px-3 py-2">Entrants</th>
                                            <th className="px-3 py-2">Scores</th>
                                            <th className="px-3 py-2">Status</th>
                                            <th className="px-3 py-2">Source</th>
                                            <th className="px-3 py-2">Confidence</th>
                                            <th className="px-3 py-2">Geo</th>
                                            <th className="px-3 py-2">Device</th>
                                            <th className="px-3 py-2">User</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r) => (
                                            <tr key={r.uid} className="border-b border-default-100 last:border-0">
                                                <td className="whitespace-nowrap px-3 py-2 text-default-500">
                                                    {r.created_at ? new Date(r.created_at).toLocaleString(undefined, {day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'}) : '—'}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-2">
                                                    {r.variant === 'pricing'
                                                        ? <Chip color="warning" size="sm" variant="flat">Pricing</Chip>
                                                        : r.variant === 'community'
                                                        ? <Chip color="secondary" size="sm" variant="flat">Community</Chip>
                                                        : <Chip color="default" size="sm" variant="flat">Main</Chip>}
                                                </td>
                                                {Object.keys(columnTitles).map((k) => (
                                                    <td key={k} className="px-3 py-2">{answerCell(r, k)}</td>
                                                ))}
                                                <td className="whitespace-nowrap px-3 py-2">
                                                    {r.mode === 'advanced'
                                                        ? <Chip color="secondary" size="sm" variant="flat">Advanced</Chip>
                                                        : r.mode === 'basic'
                                                            ? <Chip color="primary" size="sm" variant="flat">Basic</Chip>
                                                            : <span className="text-default-300">—</span>}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-2">
                                                    {r.pass_paid
                                                        ? <Chip color="success" size="sm" variant="flat">Paid</Chip>
                                                        : <Chip color="default" size="sm" variant="flat">FREE</Chip>}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-2">
                                                    {r.tournament_uid
                                                        ? (r.draw_created
                                                            ? <Chip color="success" size="sm" variant="flat">Yes</Chip>
                                                            : <Chip color="default" size="sm" variant="flat">No</Chip>)
                                                        : <span className="text-default-300">—</span>}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-2">
                                                    {r.entrants != null
                                                        ? <span className="tabular-nums font-medium">{r.entrants}</span>
                                                        : <span className="text-default-300">—</span>}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-2">
                                                    {r.tournament_uid
                                                        ? (r.has_scores
                                                            ? <Chip color="success" size="sm" variant="flat">Yes</Chip>
                                                            : <Chip color="default" size="sm" variant="flat">No</Chip>)
                                                        : <span className="text-default-300">—</span>}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-2">
                                                    <div className="flex flex-wrap items-center gap-1">
                                                        {/* "Completed" hid the funnel's most expensive drop: it meant
                                                            "finished all 5 questions, reached signup, and LEFT". Named
                                                            for what happened, and distinguished from mid-quiz quits. */}
                                                        {r.converted_at
                                                            ? <Chip color="success" size="sm" variant="flat">Signed up</Chip>
                                                            : r.completed
                                                                ? <Chip color="danger" size="sm" variant="flat">Left at signup</Chip>
                                                                : <Chip color="warning" size="sm" variant="flat">Dropped mid-quiz</Chip>}
                                                        {r.exit_reason && (
                                                            <span className="text-xs text-default-500" title={r.exit_note || undefined}>
                                                                · {EXIT_LABEL[r.exit_reason] || r.exit_reason}{r.exit_note ? ': “' + r.exit_note + '”' : ''}
                                                            </span>
                                                        )}
                                                        {r.tournament_uid && (
                                                            // League uids are 'el-…', tournaments 'et-…' — the quiz records either in this column.
                                                            r.tournament_uid.startsWith('el-')
                                                                ? <Link to={`/leagues/${r.tournament_uid}/home` as any} className="text-xs text-primary underline">league</Link>
                                                                : <Link to={`/tournaments/${r.tournament_uid}/draws` as any} className="text-xs text-primary underline">tournament</Link>
                                                        )}
                                                    </div>
                                                </td>
                                                {/* Stored attribution (users_analytics join) beats the derived guess, and the
                                                    two must not look alike: stored gets the click-id chip, derived is marked. */}
                                                <td className="max-w-[200px] px-3 py-2" title={r.source_url || undefined}>
                                                    {r.attribution ? (
                                                        <span className="flex items-center gap-1">
                                                            <span className="truncate text-default-700">{r.attribution.utm_campaign || r.attribution.utm_source || '—'}</span>
                                                            {r.attribution.click_id_type && (
                                                                <Chip color="success" size="sm" variant="flat">{r.attribution.click_id_type}</Chip>
                                                            )}
                                                        </span>
                                                    ) : r.campaign ? (
                                                        <span className="flex items-center gap-1">
                                                            <span className="truncate text-default-500">{r.campaign}</span>
                                                            <span className="text-[10px] uppercase tracking-wider text-default-400">derived</span>
                                                        </span>
                                                    ) : <span className="text-default-300">—</span>}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-2">
                                                    {r.attribution?.confidence && CONFIDENCE_CHIP[r.attribution.confidence]
                                                        ? <Chip color={CONFIDENCE_CHIP[r.attribution.confidence].color} size="sm" variant="flat">
                                                            {CONFIDENCE_CHIP[r.attribution.confidence].label}
                                                        </Chip>
                                                        : <span className="text-default-300">—</span>}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-2">
                                                    {r.country_code
                                                        ? <Chip size="sm" variant="flat">{r.country_code}</Chip>
                                                        : <span className="text-default-300">—</span>}
                                                </td>
                                                {/* Full UA on hover; the cell shows the "Windows · Chrome" summary. */}
                                                <td className="whitespace-nowrap px-3 py-2 text-default-500" title={r.user_agent || undefined}>
                                                    {deviceLabel(r.user_agent)}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-2">
                                                    {r.user
                                                        ? <Link to={`/master/users/${r.user.uid}` as any} className="text-primary">
                                                            {[r.user.name, r.user.lastname].filter(Boolean).join(' ') || r.user.email}
                                                        </Link>
                                                        : <span className="text-default-300">—</span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
}
